import './ChangePasswordModal.css'
import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useToast } from 'context/ToastContext'
import { useAuth } from 'context/AuthContext'
import apiClient from 'services/apiService'
import { updatePassword } from 'services/authService'

interface Props {
  open: boolean
  onClose: () => void
}

type ShowFields = { current: boolean; new: boolean; confirm: boolean }

const ChangePasswordModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const { logout, user } = useAuth()
  const [show, setShow] = useState<ShowFields>({ current: false, new: false, confirm: false })
  const isInDeletion = !!user?.deletedAt

  const schema = yup.object({
    currentPassword: yup.string().required(t('auth.password-required')),
    newPassword: yup
      .string()
      .min(8, t('auth.password-min'))
      .required(t('auth.password-required'))
      .test(
        'different',
        t('settings.change-password-modal.error.same-password'),
        function (value) { return value !== this.parent.currentPassword }
      ),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], t('recovery.password-match'))
      .required(t('auth.password-required')),
  })

  const formik = useFormik({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validationSchema: schema,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      try {
        const res = await updatePassword(values.currentPassword, values.newPassword)
        if (res.data.status === 1) {
          const wasRestored = res.data.wasRestored === true
          showToast(
            'success',
            t(wasRestored
              ? 'settings.change-password-modal.success-restored'
              : 'settings.change-password-modal.success')
          )
          logout()
        }
      } catch (err: any) {
        if (err?.response?.data?.status === -2) {
          setFieldError('currentPassword', t('settings.change-password-modal.error.wrong-password'))
        } else {
          showToast('error', t('settings.change-password-modal.error.bad-request'))
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleClose = () => {
    if (formik.isSubmitting) return
    formik.resetForm()
    setShow({ current: false, new: false, confirm: false })
    onClose()
  }

  const toggleShow = (field: keyof ShowFields) =>
    setShow(prev => ({ ...prev, [field]: !prev[field] }))

  const eyeAdornment = (field: keyof ShowFields) => (
    <InputAdornment position="end">
      <IconButton onClick={() => toggleShow(field)} edge="end" size="small">
        {show[field] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
      </IconButton>
    </InputAdornment>
  )

  return (
    <Modal className='test' open={open} onClose={handleClose}>
      <Box
        className="change-password-modal-box"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" color="primary">
          {t('settings.change-password-modal.title')}
        </Typography>

        {isInDeletion && (
          <Alert severity="warning" className="change-password-deletion-notice" sx={{ mt: 2 }}>
            {t('settings.change-password-modal.deletion-notice')}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Box className="change-password-fields">
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.change-password-modal.current')}
              placeholder={t('settings.change-password-modal.current-placeholder')}
              type={show.current ? 'text' : 'password'}
              {...formik.getFieldProps('currentPassword')}
              error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
              helperText={formik.touched.currentPassword && formik.errors.currentPassword}
              slotProps={{ input: { endAdornment: eyeAdornment('current') } }}
            />
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.change-password-modal.new')}
              placeholder={t('settings.change-password-modal.new-placeholder')}
              type={show.new ? 'text' : 'password'}
              {...formik.getFieldProps('newPassword')}
              error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
              helperText={formik.touched.newPassword && formik.errors.newPassword}
              slotProps={{ input: { endAdornment: eyeAdornment('new') } }}
            />
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.change-password-modal.confirm')}
              placeholder={t('settings.change-password-modal.confirm-placeholder')}
              type={show.confirm ? 'text' : 'password'}
              {...formik.getFieldProps('confirmPassword')}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              slotProps={{ input: { endAdornment: eyeAdornment('confirm') } }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" className="change-password-warning">
            {t('settings.change-password-modal.session-warning')}
          </Typography>

          <Box className="change-password-actions">
            <Button variant="outlined" size="small" onClick={handleClose} disabled={formik.isSubmitting}>
              {t('settings.change-password-modal.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="small"
              disabled={formik.isSubmitting}
              startIcon={formik.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {t(isInDeletion
                ? 'settings.change-password-modal.submit-and-restore'
                : 'settings.change-password-modal.submit')}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}

export default ChangePasswordModal
